import type { FrequencyPeriod } from 'api/recoveryAi.types'
import useFlag from 'hooks/useFlag'
import React, { useCallback, useState } from 'react'
import { useAbility } from 'utilities/ability'
import EscalationRulesCard, {
  EscalationRulesChange,
} from '../Wizard/steps/limits/EscalationRulesCard'
import {
  coerceFrequentPeriod,
  daysToPeriod,
  periodToDays,
} from '../Wizard/steps/limits/periodFormat'
import {
  clampNonNegativeThreshold,
  DEFAULT_AUTO_RESOLUTION_THRESHOLD,
  DEFAULT_ESCALATION_THRESHOLD,
} from '../Wizard/useWizardSubmit'
import { useRecoveryConfigDraft } from './RecoveryConfigDraftContext'

// Display-format fallback for the gaming period (mirrors the pane); the draft
// stores the integer day-count, the card speaks the '30d' display union.
const DEFAULT_DISPLAY_PERIOD: FrequencyPeriod = '30d'

// Escalation Rules body for the F1 Agent-Tuning modal (AI-64). Same
// EscalationRulesCard as the Settings-page EscalationRulesPane (no fork), but
// staged through RecoveryConfigDraft (Save = one PUT of changed keys; Cancel =
// rollback). Local string state keeps inputs responsive + stages the clamped
// number/day-count. Admin-only: view-only sees the card disabled (Save stays off).
const EscalationRulesModalBody = () => {
  const { draft, setField } = useRecoveryConfigDraft()
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')
  // recovery-ai-gaming-review gates the "Suspected abuse" section (mirrors the
  // Settings tab + pane). Off (default) → hidden here too.
  const suspectedAbuseEnabled = useFlag('recovery-ai-gaming-review', false)

  // The gaming fields ride along ONLY once the config exposes them (mirrors the
  // pane's gamingConfigLive sentinel) — otherwise the section stays hidden and
  // its keys are never staged, so an escalation edit can't clobber a stored
  // gaming value with an FE default (AI-302 / AI-329).
  const gamingConfigLive = draft.auto_resolution_threshold !== undefined
  // Render + stage the section only when the flag is on AND the config exposes it.
  const showGaming = suspectedAbuseEnabled && gamingConfigLive

  // Seeded once from the draft. The provider mounts a fresh draft per modal open,
  // and ConfigModalContent withholds this body until the config GET resolved, so
  // the draft already carries the saved config here.
  const [state, setState] = useState(() => ({
    frequentThreshold: String(
      draft.frequent_complaint_threshold ?? DEFAULT_ESCALATION_THRESHOLD
    ),
    frequencyPeriod: coerceFrequentPeriod(draft.frequent_complaint_period),
    repeatedThreshold: String(
      draft.repeated_complaint_threshold ?? DEFAULT_ESCALATION_THRESHOLD
    ),
    autoResolutionThreshold: String(
      draft.auto_resolution_threshold ?? DEFAULT_AUTO_RESOLUTION_THRESHOLD
    ),
    autoResolutionPeriod:
      daysToPeriod(draft.auto_resolution_period_days) ?? DEFAULT_DISPLAY_PERIOD,
  }))

  const handleChange = useCallback(
    (partial: EscalationRulesChange) => {
      // Responsive string state; stage the clamped number/day-count into the
      // draft so the committed PUT matches the pane's buildPayload.
      setState(prev => ({ ...prev, ...partial }))
      if (partial.frequentThreshold !== undefined) {
        setField(
          'frequent_complaint_threshold',
          clampNonNegativeThreshold(
            partial.frequentThreshold,
            DEFAULT_ESCALATION_THRESHOLD
          )
        )
      }
      if (partial.frequencyPeriod !== undefined) {
        setField('frequent_complaint_period', partial.frequencyPeriod)
      }
      if (partial.repeatedThreshold !== undefined) {
        setField(
          'repeated_complaint_threshold',
          clampNonNegativeThreshold(
            partial.repeatedThreshold,
            DEFAULT_ESCALATION_THRESHOLD
          )
        )
      }
      if (showGaming && partial.autoResolutionThreshold !== undefined) {
        setField(
          'auto_resolution_threshold',
          clampNonNegativeThreshold(
            partial.autoResolutionThreshold,
            DEFAULT_AUTO_RESOLUTION_THRESHOLD
          )
        )
      }
      if (showGaming && partial.autoResolutionPeriod !== undefined) {
        setField(
          'auto_resolution_period_days',
          periodToDays(partial.autoResolutionPeriod)
        )
      }
    },
    [setField, showGaming]
  )

  return (
    <EscalationRulesCard
      frequentThreshold={state.frequentThreshold}
      frequencyPeriod={state.frequencyPeriod}
      repeatedThreshold={state.repeatedThreshold}
      autoResolutionThreshold={
        showGaming ? state.autoResolutionThreshold : undefined
      }
      autoResolutionPeriod={showGaming ? state.autoResolutionPeriod : undefined}
      onChange={handleChange}
      disabled={!canManage}
    />
  )
}

export default EscalationRulesModalBody
